import React, { useEffect } from "react";
import { makeStyles, MenuItem, Typography } from "@material-ui/core";
import { KeyboardDatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import FormBlockComponent from "../../../common/FormBlockComponent";
import { useIntl } from "react-intl";
import DateFnsUtils from "@date-io/date-fns";
import Select from "@material-ui/core/Select";
import Grid from "@material-ui/core/Grid";
import { useDispatch, useSelector } from "react-redux";
import {
  storeBirthDateActionCreator,
  storeBirthZipCodeActionCreator,
  storeCommentsActionCreator,
  storeGenderActionCreator,
  storePatientDiagnosisActionCreator,
  storePatientIdActionCreator,
  storePatientInfoAction,
  storeResidentialZipCodeActionCreator,
} from "../../../../reducers/patientState";
import Button from "@material-ui/core/Button";
import { storeUserFoundActionCreator } from "../../../../reducers/commonState";
import { GENDER_FEMALE, GENDER_MALE } from "../../../../utils/constants";
import TagListComponent from "../../../common/TagListCompoents";

const useStyles = makeStyles((theme) => ({
  dataContainer: {},
  dataHeader: {
    display: "flex",
    gap: theme.spacing(1),
  },
  headerSubtitle: {
    color: theme.palette.secondary.main,
  },
  dataForm: {
    display: "flex",
    minHeight: 400,
  },
  gridItem: {
    width: "100%",
  },
  formBlock: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
  },
  textField: {
    border: `2px solid ${theme.palette.primary.main}`,
    borderRadius: 20,
    width: "100%",
    "& .Mui-focused": {
      borderRadius: 20,
      borderColor: theme.palette.secondary.main,
    },
    "&:focus-visible": {
      borderColor: "#2820D1 !important",
    },
    "& label.Mui-focused": {
      borderRadius: 20,
      color: theme.palette.secondary.main,
    },
    "& .MuiOutlinedInput-root": {
      "&.Mui-focused fieldset": {
        borderRadius: 20,
        borderColor: theme.palette.secondary.main,
      },
    },
  },
  textArea: {
    border: `2px solid ${theme.palette.primary.main}`,
    borderRadius: 12,
    "& label.Mui-focused": {
      borderRadius: 20,
      color: theme.palette.secondary.main,
    },
    "& .MuiOutlinedInput-root": {
      "&.Mui-focused fieldset": {
        borderRadius: 20,
        borderColor: theme.palette.secondary.main,
      },
    },
  },
  diagnosisTextBlock: {
    display: "flex",
    alignItems: "flex-start",
    flexDirection: "column",
  },
  diagnosisText: {
    fontWeight: "bold",
  },
  addNewUserContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  primaryButton: theme.primaryPinkButton,
}));

const PatientInfoComponent = ({ handleNewUser }) => {
  const classes = useStyles();
  const intl = useIntl();
  const dispatch = useDispatch();

  const newPatient = !!JSON.parse(localStorage.getItem("genome-new-patient"));

  const { userNotFound, originPatientList } = useSelector((state) => state.common);

  const {
    patientData,
    searchPatientId,
    patientId,
    gender,
    birthDate,
    residentialZipCode,
    birthZipCode,
    comments,
    diagnosis,
  } = useSelector((state) => state.patient);

  const handleSetPatientId = (event) => {
    dispatch(storePatientIdActionCreator(event.target.value));
  };

  const handleSetGender = (event) => {
    dispatch(storeGenderActionCreator(event.target.value));
  };

  const handleSetBirthDate = (date) => {
    const timestamp = new Date(date).getTime() / 1000;
    dispatch(storeBirthDateActionCreator(timestamp));
  };

  const handleSetResidentialZipCode = (event) => {
    dispatch(storeResidentialZipCodeActionCreator(event.target.value));
  };

  const handleSetBirthZipCode = (event) => {
    dispatch(storeBirthZipCodeActionCreator(event.target.value));
  };

  const handleSetComments = (event) => {
    dispatch(storeCommentsActionCreator(event.target.value));
  };

  useEffect(() => {
    if (!!patientData) {
      dispatch(storePatientInfoAction(patientData));
      dispatch(storePatientDiagnosisActionCreator(diagnosis || null));
    }
    !patientId && !!searchPatientId && dispatch(storePatientIdActionCreator(searchPatientId));
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddNewPatient = () => {
    localStorage.removeItem("genome-patient-id");
    dispatch(storeUserFoundActionCreator());
    handleNewUser(true);
  };

  return (
    <div className={classes.dataContainer}>
      <div className={classes.dataHeader}>
        <Typography variant={"h6"}>
          {intl.formatMessage({ id: "patient" })} {newPatient ? "" : patientData?.patient_id}
        </Typography>
        <Typography variant={"h6"} className={classes.headerSubtitle}>
          / {intl.formatMessage({ id: "patientInfo" })}
        </Typography>
      </div>
      <Grid container spacing={4} className={classes.dataForm}>
        {userNotFound ? (
          <div className={classes.addNewUserContainer}>
            <Typography variant={"h6"}>
              {intl.formatMessage(
                { id: originPatientList ? "user_deleted" : "user_not_found" },
                { id: searchPatientId },
              )}
            </Typography>
            <Button className={classes.primaryButton} onClick={handleAddNewPatient}>
              {intl.formatMessage({ id: "addNewPatient" })}
            </Button>
          </div>
        ) : (
          <>
            <Grid item sm={6} md={6} lg={4} className={classes.gridItem}>
              <FormBlockComponent value={patientId} label={"patientId"} handleChange={handleSetPatientId} required />
            </Grid>
            <Grid item sm={6} md={6} lg={4} className={classes.gridItem}>
              <div className={classes.formBlock}>
                <Typography variant={"caption"}>{intl.formatMessage({ id: "gender" })}*</Typography>
                <Select
                  id="outlined-basic"
                  variant="outlined"
                  onChange={handleSetGender}
                  value={gender}
                  className={classes.textField}
                >
                  <MenuItem value={GENDER_FEMALE}>{intl.formatMessage({ id: "gender_female" })}</MenuItem>
                  <MenuItem value={GENDER_MALE}>{intl.formatMessage({ id: "gender_male" })}</MenuItem>
                </Select>
              </div>
            </Grid>
            <Grid item sm={6} md={6} lg={4} className={classes.gridItem}>
              <div className={classes.formBlock}>
                <Typography variant={"caption"}>{intl.formatMessage({ id: "patientBirthDate" })}*</Typography>
                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                  <KeyboardDatePicker
                    className={classes.textField}
                    id="dateFromRegister"
                    inputVariant="outlined"
                    format="dd/MM/yyyy"
                    value={birthDate ? birthDate * 1000 : null}
                    onChange={(date) => handleSetBirthDate(date)}
                    invalidDateMessage={<Typography>{intl.formatMessage({ id: "incorrectDate" })}</Typography>}
                  />
                </MuiPickersUtilsProvider>
              </div>
            </Grid>
            <Grid item sm={6} md={6} lg={6} className={classes.gridItem}>
              <FormBlockComponent
                value={residentialZipCode}
                label={"postalCode_residential"}
                handleChange={handleSetResidentialZipCode}
              />
            </Grid>
            <Grid item sm={6} md={6} lg={6} className={classes.gridItem}>
              <FormBlockComponent
                value={birthZipCode}
                label={"postalCode_birth"}
                handleChange={handleSetBirthZipCode}
              />
            </Grid>
            <Grid item sm={6} className={classes.gridItem}>
              <div className={classes.diagnosisTextBlock}>
                <Typography variant={"caption"}>{intl.formatMessage({ id: "diagnosis" })}</Typography>
                <TagListComponent tagList={diagnosis} displayField={"title"} />
              </div>
            </Grid>
            <Grid item sm={6} className={classes.gridItem}>
              <FormBlockComponent minRows={3} value={comments} handleChange={handleSetComments} label={"other_info"} />
            </Grid>
            <Grid item xs={12} className={classes.gridItem}>
              <Typography variant={"caption"}>{intl.formatMessage({ id: "required_fields" })}</Typography>
            </Grid>
          </>
        )}
      </Grid>
    </div>
  );
};

export default PatientInfoComponent;
